import {
  SkyButton,
  SkyMenu,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
  SkyTooltip,
} from "@skypointcloud/skypoint-ui";
import { useIsMutating, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateRunFinished } from "@/lib/live/InstanceRunFeed";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import { useLocation, useSearch } from "wouter";
import type { ImportCauseView } from "@shared/types/importCauses";
import {
  RUN_ID_REGEX,
  STEP_NAMES,
  type EntityProgressRow,
  type RunDetail,
  type RunEventExportFormat,
  type RunEventRow,
  type RunProgressNotification,
  type RunStepRow,
  type StepName,
} from "@shared/types/runs";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { CursorPager } from "@/components/paging/CursorPager";
import { RowsTrendCard } from "@/components/runs/RowsTrendCard";
import { EntityProgressGrid } from "@/components/runs/EntityProgressGrid";
import { CancelRunDialog } from "@/components/runs/CancelRunDialog";
import { ForceResetDialog } from "@/components/runs/ForceResetDialog";
import { ImportProgress, LiveLine } from "@/components/runs/ImportProgress";
import { LiveIndicator } from "@/components/runs/LiveIndicator";
import { RetryFromFailedDialog } from "@/components/runs/RetryFromFailedDialog";
import { RetryTablesDialog } from "@/components/runs/RetryTablesDialog";
import { retryFailedTablesLabel, retryTablesLabel } from "@/components/runs/tableRuns";
import {
  causePermitted,
  type ImportCauseAction,
  type ImportCauseContext,
  type ImportCauseHandlers,
} from "@/components/runs/ImportCauseBlock";
import { RunEventFilters, hasEventFilters } from "@/components/runs/RunEventFilters";
import { RunFailureCard, type WhatToDoAccess } from "@/components/runs/RunFailureCard";
import { RunIncidentNote } from "@/components/runs/RunIncidentNote";
import { RunLeftOutNote } from "@/components/runs/RunLeftOutNote";
import { RunEventsLog } from "@/components/runs/RunEventsLog";
import {
  RunInstanceSwitchNotice,
  runInstanceLink,
  withoutInstanceParam,
} from "@/components/runs/RunInstanceSwitchNotice";
import {
  RunOutcomeCallout,
  calloutFixFirst,
  isTransformOnlyRun,
  outcomeAnnouncement,
} from "@/components/runs/RunOutcomeCallout";
import { RunStepDetails } from "@/components/runs/RunStepDetails";
import { RefreshFallbackNote } from "@/components/runs/RefreshFallbackNote";
import { SchemaChangeNote } from "@/components/runs/SchemaChangeNote";
import { RunSummaryCard } from "@/components/runs/RunSummaryCard";
import { TransformResultsView } from "@/components/runs/TransformResultsView";
import {
  RETRY_REASON_CANCELLED,
  failedStepTargets,
  retryDisabledReason,
  wasForceReset,
  type RetryTarget,
} from "@/components/runs/retryTargets";
import {
  isTerminalRun,
  loadHadNoDestination,
  loadsSendingFiles,
  loadsWaitingForSlot,
} from "@/components/runs/runFormatting";
import { PageCrumb } from "@/components/shell/PageCrumb";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { pagerRange, useCursorPager, useNextPage } from "@/hooks/use-cursor-pager";
import { usePermissions } from "@/hooks/use-permissions";
import { useRunProgress } from "@/hooks/use-run-progress";
import {
  EVENTS_PAGE_SIZE,
  EVENTS_PAGE_SIZES,
  RUN_POLL_INTERVAL_LIVE_MS,
  RUN_POLL_INTERVAL_MS,
  runEventsPath,
  runTransformResultsPath,
  useCancelRun,
  retryStepMutationKey,
  useForceResetRun,
  useRun,
  useRunEventsExport,
  useRunEventsPage,
  useRunStepAttempts,
  useRunTransformResults,
  usePollWhile,
  type RunEventFilterValues,
} from "@/hooks/use-runs";
import { useInstanceConnections, useSchedules } from "@/hooks/use-schedules";
import { useConnectors } from "@/hooks/use-connectors";
import { failureMessage } from "@/lib/api";
import { useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { CancelIcon, DownloadIcon, ResetIcon, RetryIcon } from "@/lib/icons";
import { ROUTES, absolutePath, connectionPath } from "@/lib/routes";
import { RecordNotFound, isNotFoundError } from "@/components/access/RecordNotFound";
import {
  deriveRunActivity,
  emptyFrames,
  foldFrame,
  isActivityLive,
  withLiveLoads,
  type LiveRunFrames,
} from "@/lib/runs/runActivity";
import { connectorNamesByConnection, type DestinationKind } from "@/lib/runs/stepVocabulary";
import { useDestinationSummary } from "@/hooks/use-binding";

/**
 * The run log page (UX-5, FR-53, FR-54, FR-55; Session 10, P11, ux-s10.md §0-§1).
 *
 * One page for connector, scheduler and transformation runs, read top-down in the order a person
 * asks: what happened (the outcome callout), how far it got (the step timeline and its details),
 * which tables or models (the views), and the evidence (the events, as sentences, filtered on the
 * server and downloadable).
 *
 * **Steps are this run's own `run_steps` rows**, never the five step names: one connection reads
 * "of 3", a schedule of three connections with transformations "of 8" (ux §0). The timeline is
 * P12's `ImportProgress density="page"`, fed by the same `deriveRunActivity` every other surface
 * reads, so the run page, the connection page and the list cannot disagree about one run.
 *
 * The page has two transports and only one source of truth. `GET /runs/:runId` is the record; the
 * WebSocket is a hint that saves a request (its step frames move the timeline between reads).
 * **The moment the socket is not live, the run query polls every five seconds**, so the page is
 * never waiting on a transport that is not coming back; while it is live, every fifteen. Neither
 * transport runs once the run is terminal.
 *
 * **One polite announcement per step transition**: the sr-only line below the timeline, whose text
 * is the callout's sentence and changes only on a step or phase change. The callout's own live
 * attributes are removed (S2), and the timeline's strip line is off (`announce={false}`). Focus
 * never moves on a live update. Every action is absent, not disabled, without its permission.
 */

/**
 * The destination a load step of this run named, for "Load into Snowflake" (ux §0). The runner
 * writes `metrics.destination` onto a load step once it has run (`runner/steps/startLoad.ts`), and
 * `GET /runs/:runId` returns the metrics unaudited, so the page reads no binding for it. Before
 * any load of the run has finished, the instance's destination type from the unaudited
 * destination summary names it (#1236: a step has one name, running or finished). "none" (a load
 * with no destination) and an unknown destination keep "the destination".
 */
function loadDestination(
  steps: readonly RunStepRow[] | undefined,
  instanceDestination: DestinationKind | null,
): DestinationKind | null {
  for (const row of steps ?? []) {
    if (row.step !== "load" || row.metrics === null || typeof row.metrics !== "object") continue;
    const named = (row.metrics as { destination?: unknown }).destination;
    if (named === "snowflake" || named === "databricks") return named;
  }
  return loadHadNoDestination(steps) ? null : instanceDestination;
}

function DetailSkeleton() {
  return (
    <SkySkeleton>
      <div className="rounded-lg border border-border bg-card p-4">
        <SkySkeletonItem size={20} width={320} />
      </div>
      <div className="h-4" />
      <div className="rounded-lg border border-border bg-card p-4">
        <SkySkeletonItem size={20} width={220} />
        <div className="h-4" />
        <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-4 sm:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map((cell) => (
            <div key={cell}>
              <SkySkeletonItem size={9} width={56} />
              <div className="h-2" />
              <SkySkeletonItem size={14} width={76} />
            </div>
          ))}
        </div>
      </div>
      <div className="h-4" />
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 lg:w-1/3">
        {[0, 1, 2].map((step) => (
          <div key={step} className="flex items-center gap-3">
            <SkySkeletonItem shape="circle" size={28} />
            <SkySkeletonItem size={12} width={160} />
          </div>
        ))}
      </div>
    </SkySkeleton>
  );
}

type DetailTab = "entities" | "transform" | "events";

const DETAIL_TABS: readonly DetailTab[] = ["entities", "transform", "events"];

/**
 * Session 11 (N8): the page's initial view and Events step filter from its address, so a link
 * such as the Transformations History's "View model logs"
 * (`/runs/{id}?view=events&step=transform`) opens where it points. Read once, on mount; an
 * unknown value is ignored. Nothing else of the page reads or writes the address.
 */
function initialTabFrom(search: string): DetailTab {
  const view = new URLSearchParams(search).get("view");
  return DETAIL_TABS.find((tab) => tab === view) ?? "entities";
}

function initialEventFiltersFrom(search: string): RunEventFilterValues {
  const step = new URLSearchParams(search).get("step");
  const known = STEP_NAMES.find((name) => name === step);
  return known ? { step: known } : {};
}

const RETRY_REASON_ID = "run-retry-from-failed-reason";

/** The frames this page has folded, and the notification it folded last. */
interface FoldedFrames {
  source: readonly RunProgressNotification[] | null;
  frames: LiveRunFrames | null;
}

/**
 * The view SHOWN, not the one last pressed: a run with no transform step has no dbt results, and a
 * transformation run (round 6, W5) imports no table, so it opens on its models.
 */
function shownTab(tab: DetailTab, hasTransform: boolean, transformOnly: boolean): DetailTab {
  if (tab === "transform" && !hasTransform) return "entities";
  if (tab === "entities" && transformOnly) return "transform";
  return tab;
}

/** The transform step's status, attempt and whether the run is over, or null without the step. */
function transformSignatureOf(
  transformStep: RunStepRow | undefined,
  terminal: boolean,
): string | null {
  if (!transformStep) return null;
  return `${transformStep.status}:${transformStep.attempt}:${terminal ? "terminal" : "moving"}`;
}

/** The capacity event's count of imports ahead, when it carries one. */
function queuedAheadOf(rows: RunEventRow[] | undefined): number | null {
  const value = (rows?.[0]?.payload as { queuedAhead?: unknown } | null)?.queuedAhead;
  return typeof value === "number" ? value : null;
}

/** One connection names its extract; a run of several names each step's connection instead. */
function onlyConnectionName(
  run: { dataflowIds: string[] } | undefined,
  connectionNames: ReadonlyMap<string, string>,
): string | null {
  if (!run || run.dataflowIds.length !== 1) return null;
  return connectionNames.get(run.dataflowIds[0] as string) ?? null;
}

/**
 * `?retry=1` (the runs list's "Retry from failed step", Session 10) opens the retry dialog ONCE,
 * as the page's own action would, and only when that action would be offered and enabled;
 * otherwise the page shows the action with its reason. The flag is then dropped from the address,
 * so a reload or Back does not open the dialog again. Decided during render once the run is
 * loaded (react.dev, "You Might Not Need an Effect"); only the address change, which is the
 * router's state and not this component's, waits for an effect.
 */
function useRetryFromLink({
  search,
  run,
  canRetry,
  onOpen,
  navigate,
  runId,
}: {
  search: string;
  run: RunDetail | undefined;
  canRetry: boolean;
  onOpen: (targets: RetryTarget[]) => void;
  navigate: ReturnType<typeof useLocation>[1];
  runId: string;
}): void {
  const retryRequested = new URLSearchParams(search).get("retry") === "1";
  const [retryHandled, setRetryHandled] = useState(false);
  if (retryRequested && !retryHandled && run) {
    setRetryHandled(true);
    const targets = failedStepTargets(run.steps, run);
    const offered =
      canRetry &&
      isTerminalRun(run.status) &&
      run.status !== "succeeded" &&
      !retryDisabledReason(run, run.steps, canRetry) &&
      targets.length > 0;
    if (offered) onOpen(targets);
  }
  useEffect(() => {
    if (retryRequested && retryHandled) navigate(absolutePath(`/runs/${runId}`), { replace: true });
  }, [retryRequested, retryHandled, navigate, runId]);
}

/** Before the run shows: another instance's link, the instance still resolving, loading, or its failure. */
function runGate(
  instanceLink: ReturnType<typeof runInstanceLink>,
  onSwitch: (instanceId: string) => void,
  run: ReturnType<typeof useRun>,
  idMalformed: boolean,
): ReactElement | null {
  // #1652: a malformed id and a well formed missing one read the same, with no Try again.
  if (idMalformed || (run.isError && !run.data && isNotFoundError(run.error))) {
    return (
      <RecordNotFound
        noun="run"
        listLabel="Back to Runs"
        listPath={ROUTES.runs}
        testId="run-not-found"
      />
    );
  }
  if (instanceLink.kind === "other" || instanceLink.kind === "no_access") {
    return <RunInstanceSwitchNotice link={instanceLink} onSwitch={onSwitch} />;
  }
  if (instanceLink.kind === "unresolved") return <DetailSkeleton />;

  if (run.isLoading && !run.data) return <DetailSkeleton />;

  if (run.isError && !run.data) {
    return (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        title="We could not load this run"
        message={failureMessage(run.error, "Try again.")}
        actions={
          <SkyButton size="small" onClick={() => void run.refetch()}>
            Try again
          </SkyButton>
        }
        testId="run-load-error"
      />
    );
  }

  return null;
}

/** "Retry from failed step" (#1202: the one label of the action) for the first failed step, or null when there is none. */
function stepRetryAction(
  failedTargets: RetryTarget[],
  onRetry: () => void,
  busy: boolean,
): { label: string; onRetry: () => void; busy: boolean } | null {
  const first = failedTargets[0];
  if (!first) return null;
  return { label: "Retry from failed step", onRetry, busy };
}

/** The views this run has: Tables (not for a transformation run), dbt results, Events. */
function detailViews(transformOnly: boolean, hasTransform: boolean): Array<[DetailTab, string]> {
  return [
    ...(transformOnly ? [] : ([["entities", "Tables"]] as Array<[DetailTab, string]>)),
    ...(hasTransform ? ([["transform", "dbt results"]] as Array<[DetailTab, string]>) : []),
    ["events", "Events"],
  ];
}

/** Cancel, Force reset and Retry from failed step, each only when it applies. */
function RunHeaderButtons({
  canCancel,
  canForceReset,
  cancelling,
  onCancel,
  onForceReset,
  retry,
  secondary = false,
}: {
  /** The reader may cancel, and the run is still moving. */
  canCancel: boolean;
  canForceReset: boolean;
  cancelling: boolean;
  onCancel: () => void;
  onForceReset: () => void;
  /** The retry action while it is offered: disabled with its reason, or enabled. */
  retry: { reason: string | null; busy: boolean; disabled: boolean; onRetry: () => void } | null;
  /** #1084: the shown cause's own action leads (`calloutFixFirst`), so the retry is secondary. */
  secondary?: boolean;
}) {
  return (
    <>
      {/* Absent, not disabled, for a Viewer: a greyed Cancel invites a
        support ticket asking why it does nothing. */}
      {canCancel ? (
        <SkyButton
          appearance="secondary"
          icon={CancelIcon}
          loading={cancelling}
          onClick={onCancel}
          testId="run-cancel"
        >
          Cancel run
        </SkyButton>
      ) : null}
      {canForceReset ? (
        <SkyButton
          appearance="subtle"
          icon={ResetIcon}
          onClick={onForceReset}
          testId="run-force-reset"
        >
          Force reset
        </SkyButton>
      ) : null}
      {retry?.reason ? (
        // A disabled button takes no pointer or focus events, so the tooltip hangs off a
        // focusable wrapper, and the reason is also tied to the button for a screen reader.
        <SkyTooltip content={retry.reason} testId="run-retry-from-failed-tooltip">
          <span tabIndex={0} className="inline-flex">
            <SkyButton
              appearance={secondary ? "secondary" : "primary"}
              icon={RetryIcon}
              disabled
              aria-describedby={RETRY_REASON_ID}
              testId="run-retry-from-failed"
            >
              Retry from failed step
            </SkyButton>
            <span id={RETRY_REASON_ID} className="sr-only">
              {retry.reason}
            </span>
          </span>
        </SkyTooltip>
      ) : null}
      {retry && !retry.reason ? (
        <SkyButton
          appearance={secondary ? "secondary" : "primary"}
          icon={RetryIcon}
          loading={retry.busy}
          disabled={retry.disabled}
          onClick={retry.onRetry}
          testId="run-retry-from-failed"
        >
          Retry from failed step
        </SkyButton>
      ) : null}
    </>
  );
}

/** Folds a flushed batch, frame by frame in order (another run's frames start again). */
function foldEvent(
  folded: FoldedFrames,
  runId: string,
  batch: readonly RunProgressNotification[],
): FoldedFrames {
  const base = folded.frames && folded.frames.runId === runId ? folded.frames : emptyFrames(runId);
  return { source: batch, frames: batch.reduce((frames, frame) => foldFrame(frames, frame), base) };
}

/**
 * "Retry from the failed step" (FR-54, Session 7 O4). Absent without the permission, while the
 * run moves (Cancel and Force reset hold the slot) and once it succeeded; disabled, with the
 * reason, on a finished run that has nothing to retry. W5: one retry on a transformation run (the
 * header's says all the callout's would). Product gaps row 2: the tables that did not land, as a
 * new run, for a finished run in a deployment with table runs on and a reader with `runs.retry`.
 */
function retryOffers({
  detail,
  canRetry,
  terminal,
  transformOnly,
  retrying,
  openTargets,
  openTables,
}: {
  detail: RunDetail;
  canRetry: boolean;
  terminal: boolean;
  transformOnly: boolean;
  retrying: boolean;
  openTargets: (targets: RetryTarget[]) => void;
  openTables: () => void;
}) {
  const runWasForceReset = wasForceReset(detail, detail.steps);
  const failedTargets = failedStepTargets(detail.steps, detail);
  const retryReason = retryDisabledReason(detail, detail.steps, canRetry);
  const showRetryAction = canRetry && terminal && detail.status !== "succeeded";
  const onRetry = () => openTargets(failedTargets);
  const calloutRetry =
    showRetryAction && !retryReason && !transformOnly
      ? stepRetryAction(failedTargets, onRetry, retrying)
      : null;
  // #1200: a table retry is a new run of the same connection, which the server refuses while
  // another run holds it, so it is not offered then either.
  const tablesLabel =
    canRetry && terminal && !detail.heldByRunId ? retryTablesLabel(detail.retryable) : null;
  const calloutRetryTables = tablesLabel ? { label: tablesLabel, onOpen: openTables } : null;
  const headerRetry = showRetryAction
    ? {
        reason: retryReason,
        busy: retrying,
        disabled: retrying || failedTargets.length === 0,
        onRetry,
      }
    : null;
  // #1230: the reason is drawn in the callout too, not only in the disabled button's tooltip. A
  // plain cancelled run's callout already says to start a new import, so it is not said twice.
  const retryOff =
    headerRetry?.reason && headerRetry.reason !== RETRY_REASON_CANCELLED
      ? headerRetry.reason
      : null;
  // PR #1296 review: while another run holds a connection, the callout links to it. Not on a
  // force-reset or cancelled run, whose reason does not change when that run ends.
  const heldByRunId =
    retryOff && detail.heldByRunId && !runWasForceReset && detail.status !== "cancelled"
      ? detail.heldByRunId
      : null;
  return { runWasForceReset, calloutRetry, calloutRetryTables, headerRetry, retryOff, heldByRunId };
}

/** The Steps card: the timeline, each step's details, and the page's one polite line. */
function StepsSection({
  activity,
  timelineName,
  detail,
  canRetry,
  retryBlocked,
  terminal,
  onRetry,
  transformCause,
  causes,
  announce,
  attempts,
}: {
  attempts: ComponentProps<typeof RunStepDetails>["attempts"];
  activity: ReturnType<typeof deriveRunActivity>;
  timelineName: string;
  detail: RunDetail;
  canRetry: boolean;
  retryBlocked: boolean;
  terminal: boolean;
  onRetry: (step: StepName, dataflowId: string | null) => void;
  transformCause: ComponentProps<typeof RunStepDetails>["transformCause"];
  causes: ImportCauseHandlers;
  /** The page has seen the run move, so its line speaks. */
  announce: boolean;
}) {
  return (
    <section
      aria-labelledby="run-steps-title"
      className="flex min-w-0 flex-col gap-4 rounded-lg border border-border bg-card p-4"
      data-testid="run-stepper"
    >
      <SkyText as="h2" size={400} weight="semibold" block id="run-steps-title">
        Steps
      </SkyText>
      <ImportProgress
        activity={activity}
        density="page"
        connectionName={timelineName}
        announce={false}
        testId="run-timeline"
      />
      <RunStepDetails
        run={detail}
        steps={activity.steps}
        // W5, walk 2026-10-02 D12: a transformation run has one step to retry, and the
        // header's "Retry from failed step" is it; the step's own button would be a second.
        canRetry={canRetry}
        retryBlocked={retryBlocked}
        runTerminal={terminal}
        onRetry={onRetry}
        transformCause={transformCause}
        causes={causes}
        attempts={attempts}
      />
      {/* The page's ONE polite line: the callout's sentence, once per step or phase. */}
      <LiveLine
        text={announce ? outcomeAnnouncement(activity, detail) : ""}
        testId="run-progress-announcement"
      />
    </section>
  );
}

/** The Events view: one page of the run's log, its filters, its position and pager. */
function RunEventsView({
  rows,
  events,
  nextError,
  onNextPage,
  total,
  range,
  pager,
  hasNext,
  nextPending,
  filters,
  onClearFilters,
  connectionNames,
  filterBar,
  transformOnly,
}: {
  transformOnly: boolean;
  rows: RunEventRow[];
  events: ReturnType<typeof useRunEventsPage>;
  nextError: Error | null;
  onNextPage: () => void;
  total: number | null;
  range: { start: number; end: number };
  pager: ReturnType<typeof useCursorPager>;
  hasNext: boolean;
  nextPending: boolean;
  filters: RunEventFilterValues;
  onClearFilters: () => void;
  connectionNames: ReadonlyMap<string, string>;
  filterBar: ReactElement;
}) {
  return (
    <RunEventsLog
      scope={{ transformOnly, connectionNames }}
      events={rows}
      isLoading={events.isLoading}
      error={nextError ?? events.error ?? null}
      errorTitle={
        nextError ? "We could not load the next page of events" : "We could not load these events"
      }
      onRetry={() => {
        if (nextError) onNextPage();
        else void events.refetch();
      }}
      position={
        total !== null && rows.length > 0 ? `${range.start}-${range.end} of ${total}` : null
      }
      pager={
        rows.length > 0 || !pager.isFirstPage ? (
          <CursorPager
            testId="run-events-pager"
            label="Run events pages"
            noun="events"
            start={range.start}
            end={range.end}
            total={total}
            pageSize={pager.pageSize}
            pageSizes={pager.pageSizes}
            onPageSizeChange={pager.setPageSize}
            hasPrevious={!pager.isFirstPage}
            onPrevious={pager.previous}
            hasNext={hasNext}
            onNext={onNextPage}
            nextPending={nextPending}
            className="mt-3"
          />
        ) : null
      }
      order={filters.order ?? "newest"}
      filtered={hasEventFilters(filters)}
      onClearFilters={onClearFilters}
      connectionNames={connectionNames}
      filterBar={filterBar}
    />
  );
}

/** The page's dialogs: retry from failed, retry tables and force reset, each while open. */
function RunDialogs({
  detail,
  connectionNames,
  retryTargets,
  onCloseRetry,
  retryTablesOpen,
  onRetryTablesOpenChange,
  resetOpen,
  onResetOpenChange,
  resetting,
  onReset,
  resetError,
}: {
  detail: RunDetail;
  connectionNames: Map<string, string>;
  retryTargets: RetryTarget[] | null;
  onCloseRetry: () => void;
  retryTablesOpen: boolean;
  onRetryTablesOpenChange: (open: boolean) => void;
  resetOpen: boolean;
  onResetOpenChange: (open: boolean) => void;
  resetting: boolean;
  onReset: (reason: string) => void;
  resetError: ReturnType<typeof useInlineError>["error"];
}) {
  return (
    <>
      {retryTargets ? (
        <RetryFromFailedDialog
          open
          onOpenChange={(open) => {
            if (!open) onCloseRetry();
          }}
          run={detail}
          targets={retryTargets}
          connectionNames={connectionNames}
        />
      ) : null}

      {retryTablesOpen ? (
        <RetryTablesDialog
          open
          onOpenChange={onRetryTablesOpenChange}
          run={detail}
          connectionNames={connectionNames}
        />
      ) : null}

      {resetOpen ? (
        <ForceResetDialog
          open
          onOpenChange={onResetOpenChange}
          runId={detail.runId}
          isPending={resetting}
          onConfirm={onReset}
          error={resetError}
        />
      ) : null}
    </>
  );
}

/**
 * The run, read on a timer while it moves, its live progress socket (none for a finished run:
 * it has nothing left to push), and the runner's frames folded in place (Session 10, P12) so the
 * timeline moves between reads; each notification is folded once, during render.
 */
function useLiveRun(runId: string, linkBlocks: boolean, instanceId: string | null) {
  const run = useRun(linkBlocks ? null : runId);
  const terminal = run.data ? isTerminalRun(run.data.status) : false;

  // #1043: a run that finishes while the page is open has just written its load records, so what
  // the run history queries cached up to a minute ago is stale. The page stays mounted across the
  // tabs, so it (not the Tables card) invalidates once on the flip to finished: a mounted card
  // reads again at once, an unmounted one or another screen reads when it next opens.
  const queryClient = useQueryClient();
  const wasTerminal = useRef<{ runId: string; terminal: boolean }>({ runId, terminal });
  useEffect(() => {
    const previous = wasTerminal.current;
    wasTerminal.current = { runId, terminal };
    if (!terminal || previous.runId !== runId || previous.terminal) return;
    // #2005: and the lists, so going back to the Runs list (or any list) within its staleTime
    // reads the finished run, not the Running row it cached before this page opened.
    invalidateRunFinished(queryClient);
  }, [runId, terminal, queryClient]);

  // No socket for a finished run: it has nothing left to push.
  const progress = useRunProgress({
    runId,
    instanceId,
    enabled: Boolean(run.data) && !terminal,
  });

  // A run that is still moving is re-read on a timer, socket or no socket: the socket's frames
  // are a hint (NOTIFY is at-most-once), never the record. The socket buys a slower interval.
  usePollWhile(
    !terminal && Boolean(run.data),
    progress.isLive ? RUN_POLL_INTERVAL_LIVE_MS : RUN_POLL_INTERVAL_MS,
    () => {
      void run.refetch();
    },
  );

  // PR #1296 review: a finished run that another run holds a connection of (`heldByRunId`, #1200)
  // keeps Retry off only until that run ends, and nothing else re-reads a finished run (no socket,
  // no focus refetch), so it is re-read on the slow timer while the server says it is held.
  usePollWhile(terminal && Boolean(run.data?.heldByRunId), RUN_POLL_INTERVAL_MS, () => {
    void run.refetch();
  });

  // The runner's step and run frames (Session 10, P12) folded in place, so the timeline moves
  // between reads. Adjusted during render: each notification is folded once.
  const [folded, setFolded] = useState<FoldedFrames>({ source: null, frames: null });
  const batch = progress.batch;
  if (batch.length > 0 && batch !== folded.source) setFolded(foldEvent(folded, runId, batch));

  return { run, terminal, progress, folded };
}

/**
 * The Events view's page: gated like the run itself (contract-notif R12, review r2 N1), one page at
 * a time over the server's cursor (round 4, 10c), and only the first page re-read while the run
 * moves (newest first, new events land on top; a later page never shifts under the reader).
 */
function useEventsPage({
  runId,
  linkBlocks,
  open,
  eventFilters,
  moving,
  live,
}: {
  runId: string;
  linkBlocks: boolean;
  open: boolean;
  eventFilters: RunEventFilterValues;
  moving: boolean;
  live: boolean;
}) {
  const eventsKey = JSON.stringify(eventFilters);
  const eventsPager = useCursorPager({
    pageSizes: EVENTS_PAGE_SIZES,
    defaultPageSize: EVENTS_PAGE_SIZE,
    resetKey: eventsKey,
  });
  const events = useRunEventsPage(
    linkBlocks ? null : runId,
    open,
    eventFilters,
    eventsPager.cursor,
    eventsPager.pageSize,
  );
  // Only the first page takes new events while the run moves (newest first, they land on top);
  // a later page is a place the reader chose and never shifts or re-reads under them.
  usePollWhile(
    open && moving && eventsPager.isFirstPage,
    live ? RUN_POLL_INTERVAL_LIVE_MS : RUN_POLL_INTERVAL_MS,
    () => {
      void events.refetch();
    },
  );
  return { eventsKey, eventsPager, events };
}

/**
 * The dbt results, read while their view is open or the transform step failed (its CauseBlock),
 * re-read while the view is open and the run moves, and invalidated once whenever the transform
 * step's signature changes (review r2 B1, below).
 */
function useTransformResults({
  runId,
  transformStep,
  open,
  terminal,
  loaded,
}: {
  runId: string;
  transformStep: RunStepRow | undefined;
  /** The dbt results view is the one shown. */
  open: boolean;
  terminal: boolean;
  /** The run has been read. */
  loaded: boolean;
}) {
  const hasTransform = transformStep !== undefined;
  const transformFailed = transformStep?.status === "failed";
  const transformResults = useRunTransformResults(runId, hasTransform && (open || transformFailed));
  usePollWhile(open && hasTransform && !terminal, RUN_POLL_INTERVAL_LIVE_MS, () => {
    void transformResults.refetch();
  });
  // Review r2 B1: the runner writes every DBT_MODEL and DBT_TEST row, and `metrics.cause`, only as
  // the transform step finishes (`runner/steps/transform.ts`, before `finish`), and the run turns
  // terminal seconds later, which stops the poll above. So the read that matters is the one AFTER
  // the step moves. When the transform step's status or attempt changes, or the run turns
  // terminal, the results are invalidated once: an open view (or a failed step's CauseBlock)
  // re-reads now, a closed one is stale and re-reads when it opens. The first signature a run
  // shows is only recorded (the query's own first read covers it), so opening a finished run
  // still reads once. `cancelRefetch: false` joins a read the same render just started.
  const queryClient = useQueryClient();
  const transformSignature = loaded ? transformSignatureOf(transformStep, terminal) : null;
  const seenTransformSignature = useRef<{ runId: string; signature: string } | null>(null);
  useEffect(() => {
    if (transformSignature === null) return;
    const previous = seenTransformSignature.current;
    seenTransformSignature.current = { runId, signature: transformSignature };
    if (!previous || previous.runId !== runId || previous.signature === transformSignature) return;
    void queryClient.invalidateQueries(
      { queryKey: [runTransformResultsPath(runId)] },
      { cancelRefetch: false },
    );
  }, [transformSignature, runId, queryClient]);
  return transformResults;
}

/** The export menu says when the file is narrowed to the Events view's filters. */
function exportNounFor(tab: DetailTab, eventFilters: RunEventFilterValues): string {
  return tab === "events" && hasEventFilters(eventFilters)
    ? "Export filtered events"
    : "Export events";
}

export default function RunDetailPage({ runId }: { runId: string }) {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const { canAny } = usePermissions();
  const { selectedInstance, instances, pendingInstanceId, selectInstance } = useTenantInstance();
  // contract-notif R12: a notification's `?instance=` link. The same instance drops the parameter;
  // another one waits for "Switch", and nothing of the run is read under the wrong instance.
  const instanceLink = runInstanceLink(pendingInstanceId, selectedInstance?.id ?? null, instances);
  // #1652: a malformed id is not asked for; the page reads not found at once.
  const idMalformed = !RUN_ID_REGEX.test(runId);
  const linkBlocks = idMalformed || (instanceLink.kind !== "none" && instanceLink.kind !== "same");
  useEffect(() => {
    if (instanceLink.kind !== "same") return;
    navigate(withoutInstanceParam(location, search), { replace: true });
  }, [instanceLink.kind, location, search, navigate]);
  const notice = useNotice();
  // Cancel and the export menu are header actions: their failure lands in the notice directly
  // under the header. A force reset's failure stays inside its open dialog (ux-s10 §3).
  const showApiError = useApiErrorNotice();
  const resetError = useInlineError();

  const [tab, setTab] = useState<DetailTab>(() => initialTabFrom(search));
  const [resetOpen, setResetOpen] = useState(false);
  // The targets the retry dialog is open for; null while it is closed. The page's action opens
  // it with every failed step's target, a step's own button with that one step.
  const [retryTargets, setRetryTargets] = useState<RetryTarget[] | null>(null);
  // Product gaps row 2: the "Retry N tables" dialog.
  const [retryTablesOpen, setRetryTablesOpen] = useState(false);
  const [eventFilters, setEventFilters] = useState<RunEventFilterValues>(() =>
    initialEventFiltersFrom(search),
  );

  const canRetry = canAny("runs.retry");
  const canCancel = canAny("runs.cancel");
  const canForceReset = canAny("runs.force_reset");
  const { exportEvents, isExporting } = useRunEventsExport();

  const { run, terminal, progress, folded } = useLiveRun(
    runId,
    linkBlocks,
    selectedInstance?.id ?? null,
  );

  const transformStep = run.data?.steps.find((step) => step.step === "transform");
  const hasTransform = transformStep !== undefined;
  // A failed transform step shows its classified cause in the step details (contract §12,
  // `CauseBlock`). The server builds that view and returns it with the transform results, so the
  // one read serves both the step details and the Transformations view (same query key).
  const transformFailed = transformStep?.status === "failed";
  // Round 6, W5: a run with no connection is a transformation run, not an import.
  const transformOnly = run.data ? isTransformOnlyRun(run.data) : false;
  // W5: a transformation run imports no table, so it has no Tables view; its models are what
  // it has to show, and they are where the page opens. The view SHOWN, not the one last pressed:
  // a transformation run lands on Transformations with `tab` still "entities", and its results
  // must load for what is on screen (walk 2026-10-02, D1).
  const view = shownTab(tab, hasTransform, transformOnly);
  // Gated like the run itself (contract-notif R12, review r2 N1): a blocked link reads nothing.
  // Round 4 (10c): one page at a time, 50/100/200, over the server's cursor; a filter or a size
  // starts again at the first page.
  const { eventsKey, eventsPager, events } = useEventsPage({
    runId,
    linkBlocks,
    open: tab === "events",
    eventFilters,
    moving: !terminal && Boolean(run.data),
    live: progress.isLive,
  });
  // #1318: how each earlier attempt of a retried step ended, read only once a step was retried.
  const stepAttempts = useRunStepAttempts(
    linkBlocks ? null : runId,
    Boolean(run.data?.steps.some((step) => step.attempt > 1)),
    !terminal && Boolean(run.data),
  );
  const transformResults = useTransformResults({
    runId,
    transformStep,
    open: view === "transform",
    terminal,
    loaded: Boolean(run.data),
  });
  // Gated like the run (review nit): a blocked link reads nothing, not even the selected
  // instance's connection and schedule names.
  const connections = useInstanceConnections(!linkBlocks);
  const schedules = useSchedules(!linkBlocks);
  // #2389: the connector names the running extract's liveness line reads ("Still reading from
  // Mouseflow."), only while the run can still move. The catalogue is cached for the session.
  const connectors = useConnectors(!linkBlocks && !terminal);

  // "Waiting to start: {q} imports ahead of this one" (ux §1): the capacity event's count, read
  // only while the run is queued. The events read is not audited.
  const queued = run.data?.status === "queued";
  const queuedCapacity = useQuery<RunEventRow[]>({
    queryKey: [`${runEventsPath(runId)}?event=RUN_QUEUED_CAPACITY&limit=1`],
    enabled: queued,
    refetchInterval: queued ? RUN_POLL_INTERVAL_MS : false,
    retry: false,
  });
  const queuedAhead = queuedAheadOf(queuedCapacity.data);

  // The dialog owns the mutation; the page only shows its action busy while it runs.
  const retrying = useIsMutating({ mutationKey: retryStepMutationKey(runId) }) > 0;
  const cancelRun = useCancelRun(runId);
  const forceReset = useForceResetRun(runId);
  const [cancelOpen, setCancelOpen] = useState(false);

  const connectionNames = useMemo(
    () =>
      new Map(
        (connections.data ?? []).map((connection) => [connection.id, connection.displayName]),
      ),
    [connections.data],
  );

  const schedule = useMemo(() => {
    if (!run.data?.scheduleId) return null;
    return (schedules.data ?? []).find((entry) => entry.id === run.data?.scheduleId) ?? null;
  }, [run.data?.scheduleId, schedules.data]);

  // One connection names its extract ("Extract from Clinical SQL"); a run of several names each
  // step's connection beside the label instead.
  const onlyConnection = onlyConnectionName(run.data, connectionNames);
  const destinationSummary = useDestinationSummary();
  // The instance's destination type; a load that runs has one, set or not.
  const instanceDestination = destinationSummary.data?.type ?? null;
  const destination = loadDestination(run.data?.steps, instanceDestination);
  const sourceNames = useMemo(
    () => connectorNamesByConnection(connections.data, connectors.data),
    [connections.data, connectors.data],
  );
  const vocabulary = useMemo(
    () => ({ connector: onlyConnection, destination, transformOnly, sourceNames }),
    [onlyConnection, destination, transformOnly, sourceNames],
  );

  const activity = useMemo(
    () =>
      deriveRunActivity({
        runId,
        dataflowId: null,
        detail: run.data,
        frames: folded.frames,
        vocabulary,
        transport: progress.transport,
        loading: run.isLoading,
        queuedAhead,
      }),
    [runId, run.data, folded.frames, vocabulary, progress.transport, run.isLoading, queuedAhead],
  );

  // The page's one polite line says a moment only once the page has seen the run move, so
  // opening a run that finished last week announces nothing (ux §1: one per step transition).
  const [sawLive, setSawLive] = useState(false);
  if (!sawLive && isActivityLive(activity)) setSawLive(true);

  const eventRows = useMemo(() => events.data?.rows ?? [], [events.data]);
  const eventsTotal = events.data?.total ?? null;
  const eventsNext = events.data?.nextCursor ?? null;
  const eventsRange = pagerRange({
    pageIndex: eventsPager.pageIndex,
    pageSize: eventsPager.pageSize,
    rowCount: eventRows.length,
  });
  // Next reads the page before the pager moves: a failure leaves the page on screen (NFR-8).
  const eventsNextPage = useNextPage({
    pager: eventsPager,
    pageKey: `${eventsKey}|${eventsPager.pageSize}`,
    total: eventsTotal,
    nextCursor: eventsNext,
    fetchPage: events.fetchPage,
  });
  const eventsNextError = eventsNextPage.error;
  const nextEventsPage = eventsNextPage.goNext;

  /** A step's own "Retry the … step" button: the same dialog, with that one step. */
  const openStepRetry = (step: StepName, dataflowId: string | null): void => {
    const row = run.data?.steps.find((s) => s.step === step && s.dataflowId === dataflowId);
    if (row) setRetryTargets([{ step, dataflowId, row }]);
  };

  const doCancel = async (): Promise<void> => {
    try {
      const result = await cancelRun.mutateAsync();
      // F53: a stop the platform refused is a warning, never folded into "0 were stopped".
      const unstopped = result.unstoppedExecutions?.length ?? 0;
      if (unstopped > 0) {
        notice.warning(
          `${result.stoppedExecutions.length} running jobs were stopped. ${unstopped} could not be stopped and may run to their own end. An import's output is not loaded.`,
          { title: "The run was cancelled." },
        );
      } else {
        notice.success(`${result.stoppedExecutions.length} running jobs were stopped.`, {
          title: "The run was cancelled.",
        });
      }
    } catch (error) {
      showApiError(error, "The run could not be cancelled.");
    }
  };

  // The menu says when the file is narrowed to the Events view's filters (review r1 nit).
  const exportNoun = exportNounFor(tab, eventFilters);

  const doExport = async (format: RunEventExportFormat): Promise<void> => {
    // Round 4: the page's one export. While the Events view is open it carries the filters on
    // screen; elsewhere it is the whole log. The export is always oldest first (it is a file to
    // read top-down); the order is the screen's, not a filter.
    const filters: RunEventFilterValues = tab === "events" ? eventFilters : {};
    const { order: _order, ...narrowing } = filters;
    // R2.3: the readable log is always the whole run; "Errors only" is the one filter it takes.
    const sent: typeof narrowing =
      format === "txt" ? { level: narrowing.level === "error" ? "error" : undefined } : narrowing;
    try {
      const filename = await exportEvents(runId, format, sent);
      notice.success(`Saved as ${filename}.`, { title: "Events exported" });
    } catch (error) {
      showApiError(error, "The events could not be exported.");
    }
  };

  // "Show events" and "Clear filters" replace the button a reader just pressed (the view switches,
  // the empty-state bar gives way to the list), so focus moves to the Events filter group rather
  // than falling to the body. Only ever after the reader's own action, never on a live update.
  const eventFiltersRef = useRef<HTMLFieldSetElement>(null);
  const [focusFiltersRequest, setFocusFiltersRequest] = useState(0);
  useEffect(() => {
    if (focusFiltersRequest > 0) eventFiltersRef.current?.focus();
  }, [focusFiltersRequest]);

  /** The Tables view's "Show events": the Events view filtered to that table. */
  const showTableEvents = (row: EntityProgressRow): void => {
    setEventFilters((previous) => ({
      ...previous,
      entityName: row.entityName,
      // A run of several connections can hold one table name twice; name the connection too.
      dataflowId: (run.data?.dataflowIds.length ?? 0) > 1 ? row.dataflowId : previous.dataflowId,
    }));
    setTab("events");
    setFocusFiltersRequest((count) => count + 1);
  };

  // `?retry=1` (the runs list's "Retry from failed step", Session 10) opens the retry dialog
  // ONCE, as the page's own action would, and only when that action would be offered and
  // enabled; otherwise the page shows the action with its reason. The flag is then dropped from
  // the address, so a reload or Back does not open the dialog again. Decided during render once
  // the run is loaded (react.dev, "You Might Not Need an Effect"); only the address change, which
  // is the router's state and not this component's, waits for an effect.
  useRetryFromLink({ search, run: run.data, canRetry, onOpen: setRetryTargets, navigate, runId });

  const doForceReset = async (reason: string): Promise<void> => {
    resetError.clear();
    try {
      const result = await forceReset.mutateAsync({ reason });
      setResetOpen(false);
      notice.success(
        // #1231: the steps a force reset stops read Cancelled, and so does the run.
        `${result.resetSteps} ${result.resetSteps === 1 ? "step was" : "steps were"} marked cancelled. The reason is in the audit trail.`,
        { title: "The run was force reset." },
      );
    } catch (error) {
      // The dialog stays open with the reason still typed; the failure is said inside it.
      resetError.show(error, "The run could not be force reset.");
    }
  };

  // Called, not rendered as a component: each state returns the element it always did.
  // The link names a run of the other instance: the address stays, and opens it there.
  const gate = runGate(
    instanceLink,
    (id) => selectInstance(id, { keepLocation: true }),
    run,
    idMalformed,
  );
  if (gate) return gate;

  // #1148: the tables carry the load outcomes this tab has seen live, the same per-table state
  // the header counts, so a table counted as loaded reads Loaded in its row.
  const detail = withLiveLoads(run.data, folded.frames);
  if (!detail) return <DetailSkeleton />;

  // "Retry from the failed step" (FR-54, Session 7 O4). Absent without the permission, while
  // the run moves (Cancel and Force reset hold the slot) and once it succeeded; disabled, with
  // the reason, on a finished run that has nothing to retry.
  const { runWasForceReset, calloutRetry, calloutRetryTables, headerRetry, retryOff, heldByRunId } =
    retryOffers({
      detail,
      canRetry,
      terminal,
      transformOnly,
      retrying,
      openTargets: setRetryTargets,
      openTables: () => setRetryTablesOpen(true),
    });

  // Product gaps row 4: what a cause's button does on this page. A cause is the server's words;
  // the page only decides where its one action goes. Retry is the table retry (row 2) when the
  // run offers it, else "Retry from {step}"; with neither, the fix stands alone.
  const causeAction = (
    cause: ImportCauseView,
    dataflowId: string | null,
    context?: ImportCauseContext,
  ): ImportCauseAction | null => {
    switch (cause.action) {
      case "open_connection":
      case "validate_connection":
      case "open_tables":
      case "resync_table":
      case "full_refresh":
        return dataflowId
          ? {
              label: cause.actionLabel,
              onClick: () => navigate(absolutePath(connectionPath(dataflowId))),
            }
          : null;
      case "open_destination":
        return {
          label: cause.actionLabel,
          onClick: () => navigate(absolutePath(ROUTES.settingsDestination)),
        };
      case "retry_failed_tables": {
        if (calloutRetryTables) {
          return {
            label: retryFailedTablesLabel(detail.retryable) ?? cause.actionLabel,
            onClick: calloutRetryTables.onOpen,
          };
        }
        // In a step's details that already draw "Retry the {step} step", a second retry would
        // repeat it (review r1 N5); the callout drops its own the same way.
        if (context?.stepRetryShown) return null;
        return calloutRetry ? { label: calloutRetry.label, onClick: calloutRetry.onRetry } : null;
      }
      case "copy_reference":
        return {
          label: cause.actionLabel,
          onClick: () => {
            void navigator.clipboard
              ?.writeText(detail.runId)
              .then(() =>
                notice.success(`${detail.runId} is on the clipboard.`, {
                  title: "Reference copied",
                }),
              )
              .catch(() => undefined);
          },
        };
    }
  };
  const causeHandlers: ImportCauseHandlers = {
    action: causeAction,
    permitted: (cause) => causePermitted(cause, (key) => canAny(key)),
  };

  // #1084: the What went wrong card's suggested action, decided as the cause block decides.
  const whatToDoAccess: WhatToDoAccess = {
    permitted: (whatToDo) =>
      causePermitted({ permission: whatToDo.permission ?? null }, (key) => canAny(key)),
  };

  // #1084: one primary action per run page. When the shown cause is the customer's to fix first
  // and this reader can take its action, that action is the primary one, and the callout's and
  // the header's retries are secondary.
  const fixFirst = calloutFixFirst(activity, detail, causeHandlers);

  const runSteps = STEP_NAMES.filter((step) => detail.steps.some((row) => row.step === step));
  const runConnections = detail.dataflowIds.map((id) => ({
    id,
    name: connectionNames.get(id) ?? id,
  }));
  const views = detailViews(transformOnly, hasTransform);
  // The progress bar's name: "Clinical SQL import: step 2 of 3", "Nightly import: …"; a run of
  // several connections started by hand reads "The import: step 2 of 8" (review r2 nit).
  const timelineName = onlyConnection ?? schedule?.name ?? "The";

  const renderView = (): ReactNode => {
    if (view === "entities") {
      return (
        <section aria-label="Tables">
          {/* #828 (R6.2): the connection's rows per load, above this run's per-table rows. */}
          <div className="mb-3">
            <RowsTrendCard
              runId={detail.runId}
              dataflowIds={detail.dataflowIds}
              connectionNames={connectionNames}
              entities={detail.entities}
              runTerminal={terminal}
            />
          </div>
          <EntityProgressGrid
            runId={detail.runId}
            rows={detail.entities}
            live={progress.entities}
            connectionNames={connectionNames}
            isLoading={run.isFetching && detail.entities.length === 0}
            runTerminal={terminal}
            noDestination={loadHadNoDestination(detail.steps)}
            waitingLoads={loadsWaitingForSlot(detail.steps)}
            sendingLoads={loadsSendingFiles(detail.steps)}
            onShowEvents={showTableEvents}
            causes={causeHandlers}
          />
        </section>
      );
    }
    if (view === "transform") {
      return (
        <TransformResultsView
          data={transformResults.data}
          isLoading={transformResults.isLoading}
          error={transformResults.error ?? null}
          onRetry={() => void transformResults.refetch()}
          runStatus={detail.status}
          transformStep={detail.steps.find((step) => step.step === "transform")}
        />
      );
    }
    return (
      <RunEventsView
        rows={eventRows}
        events={events}
        nextError={eventsNextError}
        onNextPage={() => void nextEventsPage()}
        total={eventsTotal}
        range={eventsRange}
        pager={eventsPager}
        hasNext={eventsNext !== null}
        nextPending={eventsNextPage.pending}
        filters={eventFilters}
        onClearFilters={() => {
          setEventFilters(({ order }) => ({ order }));
          setFocusFiltersRequest((count) => count + 1);
        }}
        connectionNames={connectionNames}
        transformOnly={transformOnly}
        filterBar={
          <RunEventFilters
            ref={eventFiltersRef}
            value={eventFilters}
            onChange={setEventFilters}
            steps={runSteps}
            connections={runConnections}
            full
          />
        }
      />
    );
  };

  return (
    <>
      <TopbarActionsPortal>
        <LiveIndicator
          transport={progress.transport}
          pollSeconds={Math.round(RUN_POLL_INTERVAL_MS / 1000)}
        />
        <RunHeaderButtons
          canCancel={canCancel && !terminal}
          canForceReset={canForceReset && !terminal}
          cancelling={cancelRun.isPending}
          onCancel={() => setCancelOpen(true)}
          onForceReset={() => setResetOpen(true)}
          retry={headerRetry}
          secondary={fixFirst}
        />
        {/* A read every role that sees the run may make (Session 10, decision Q2). */}
        <SkyMenu
          align="end"
          items={[
            {
              key: "download-log",
              label: "Download log (.txt)",
              icon: <DownloadIcon className="size-4" />,
              onClick: () => void doExport("txt"),
            },
            {
              key: "export-csv",
              label: `${exportNoun} (CSV)`,
              icon: <DownloadIcon className="size-4" />,
              onClick: () => void doExport("csv"),
            },
            {
              key: "export-jsonl",
              label: `${exportNoun} (JSON Lines)`,
              icon: <DownloadIcon className="size-4" />,
              onClick: () => void doExport("jsonl"),
            },
          ]}
          trigger={
            <SkyButton
              appearance="secondary"
              icon={DownloadIcon}
              loading={isExporting}
              testId="run-export-events"
            >
              Export events
            </SkyButton>
          }
        />
      </TopbarActionsPortal>

      {/* The trail is the shell's breadcrumb row (post-release 607e63a); the page names it. */}
      <PageCrumb label={detail.runId} />

      <div className="flex flex-col gap-4">
        <RunOutcomeCallout
          activity={activity}
          run={detail}
          retry={calloutRetry}
          retryTables={calloutRetryTables}
          causes={causeHandlers}
          fixFirst={fixFirst}
          retryOff={retryOff}
          heldByRunId={heldByRunId}
        />

        {/* Round 10 (R2.1): the server's failure summary, only on a failed or partial run. */}
        {detail.failure ? (
          <RunFailureCard failure={detail.failure} runId={detail.runId} access={whatToDoAccess} />
        ) : null}

        {/* #842: the schedule's open skyMonitor alert, on a failed run it covers only. */}
        <RunIncidentNote
          runId={detail.runId}
          status={detail.status}
          scheduleId={detail.scheduleId ?? null}
        />

        {/* #1673: a schedule's run names the connections it left out, and why. */}
        <RunLeftOutNote
          runId={detail.runId}
          scheduleId={detail.scheduleId ?? null}
          connectionNames={connectionNames}
        />

        {/* Round 4 item 7a: an incremental import that read everything says so, and why. */}
        <RefreshFallbackNote steps={detail.steps} />

        {/* #1663: a table whose columns changed in this import, counted; each table says what. */}
        <SchemaChangeNote
          tables={detail.schemaChangedTables ?? 0}
          stopped={detail.schemaStoppedTables ?? 0}
          succeeded={detail.status === "succeeded"}
          canAct={canAny("dataflows.write")}
        />

        <RunSummaryCard
          run={detail}
          connectionNames={connectionNames}
          scheduleName={schedule?.name ?? null}
        />

        {run.isError ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="The last refresh failed"
            message="What is shown below is the run as it was when it last loaded. The page keeps trying."
            testId="run-refresh-warning"
          />
        ) : null}

        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-3">
          <StepsSection
            activity={activity}
            timelineName={timelineName}
            detail={detail}
            canRetry={canRetry && !transformOnly}
            // #1200: a step's own retry is refused too while another run holds the connection.
            retryBlocked={
              runWasForceReset || detail.status === "cancelled" || Boolean(detail.heldByRunId)
            }
            terminal={terminal}
            onRetry={openStepRetry}
            transformCause={transformFailed ? (transformResults.data?.cause ?? null) : null}
            causes={causeHandlers}
            announce={sawLive}
            attempts={stepAttempts.data}
          />

          <div className="min-w-0 lg:col-span-2">
            {/* A pressed-button group rather than `SkyTabList`: in skypoint-ui
                2.4.0 that component emitted `aria-controls` pointing at a panel
                it expected to own, and this screen renders its own, so axe
                reported the dangling reference as `aria-valid-attr-value`
                (critical). 2.17.0 fixes that; moving to `SkyTabList` +
                `SkyTabPanel` is a backlog row. Buttons with `aria-pressed` say
                the same thing and are answerable. */}
            <fieldset
              aria-label="What to show for this run"
              className="min-w-0 flex flex-wrap items-center gap-2 border-b border-border pb-2"
              data-testid="run-detail-views"
            >
              {views.map(([value, label]) => (
                <SkyButton
                  key={value}
                  appearance={view === value ? "secondary" : "subtle"}
                  size="small"
                  aria-pressed={view === value}
                  onClick={() => setTab(value)}
                  testId={`run-view-${value}`}
                >
                  {label}
                </SkyButton>
              ))}
            </fieldset>

            <div className="mt-3">{renderView()}</div>
          </div>
        </div>
      </div>

      {cancelOpen && !terminal ? (
        <CancelRunDialog open onOpenChange={setCancelOpen} onConfirm={() => void doCancel()} />
      ) : null}
      <RunDialogs
        detail={detail}
        connectionNames={connectionNames}
        retryTargets={canRetry ? retryTargets : null}
        onCloseRetry={() => setRetryTargets(null)}
        retryTablesOpen={calloutRetryTables !== null && retryTablesOpen}
        onRetryTablesOpenChange={setRetryTablesOpen}
        resetOpen={canForceReset && resetOpen}
        onResetOpenChange={(open) => {
          setResetOpen(open);
          if (!open) resetError.clear();
        }}
        resetting={forceReset.isPending}
        onReset={(reason) => void doForceReset(reason)}
        resetError={resetError.error}
      />
    </>
  );
}
